import React, { useEffect, useMemo, useRef, useState } from 'react'
import { listUrls, urlFor, nearestAvailableUrl, dialAnchorFor } from '../../business/exercise/exerciseAvatar'
import { preloadKind } from '../../business/exercise/imageDecodeCache'
import RomBandDial from './RomBandDial'
import LinearBandBar from './LinearBandBar'
import '../../css/image-swap-figure.css'

// Renders the patient body as an image-swap PNG stack with the ROM band
// dial (green/yellow/blue sectors + red tick + angle label) overlaid on
// top in the same viewBox 0 0 200 210 coordinate system as KneeROMFigure
// so the bands stay visually consistent with the SVG figure.
//
// Props:
//   assetKind   — one of 'SITTING_LIFT' | 'SIT_TO_STAND' | 'HEEL_SLIDE' |
//                 'HEEL_SLIDE_ROM' | 'KNEE_TO_CHEST' | 'KNEE_FLEXION' |
//                 'LYING_KICKS'
//   liveAngle   — clinical degrees (drives which PNG is visible + dial tick)
//   bandRanges  — { green:[a,b], yellow:[a,b], blue:[a,b] } | null
//   maxWidth    — container max width (default 320, matches KneeROMFigure)

export default function ImageSwapFigure({ assetKind, liveAngle, bandRanges, maxWidth = 320, posture = null, goalAngles = null, yellowOutline = false, radialAngles = null, labelAngle = null, background = null }) {
  const [ready, setReady] = useState(false)
  const angle = (liveAngle == null) ? 90 : liveAngle

  // Per-kind anchor carries the dial geometry AND an optional PNG body
  // shift (imageOffsetX/Y in % of container width). The shift composes
  // with the .isf-stack scale(1.0) so the avatar can be nudged without
  // editing the CSS file.
  const anchor = dialAnchorFor(assetKind) || {}
  const imageOffsetX = anchor.imageOffsetX || 0
  const imageOffsetY = anchor.imageOffsetY || 0
  // Per-kind zoom of the PNG body (1 = native), as in the patient app. Only
  // kinds whose admin anchor sets it are affected (Mini Squat); the rest carry
  // no imageScale here and keep scale(1.0).
  const imageScale = (anchor.imageScale != null) ? anchor.imageScale : 1
  const stackStyle = (imageOffsetX || imageOffsetY || imageScale !== 1)
    ? { transform: `translate(${imageOffsetX}%, ${imageOffsetY}%) scale(${imageScale})` }
    : undefined

  // Preload + decode the entire kind's PNG set on mount. After this resolves,
  // subsequent angle changes are pure GPU opacity flips with zero decode jank.
  useEffect(() => {
    if (!assetKind) return
    let cancelled = false
    setReady(false)
    preloadKind(assetKind).then(() => { if (!cancelled) setReady(true) })
    return () => { cancelled = true }
  }, [assetKind])

  // Build the <img> stack ONCE per assetKind. useMemo keyed on assetKind only
  // — angle changes do NOT rebuild the stack.
  const urls = useMemo(() => (assetKind ? listUrls(assetKind) : []), [assetKind])

  const requestedUrl = useMemo(
    () => (assetKind ? urlFor(assetKind, angle) : null),
    [assetKind, angle]
  )
  const activeUrl = useMemo(() => {
    if (!requestedUrl) return null
    if (urls.indexOf(requestedUrl) !== -1) return requestedUrl
    return nearestAvailableUrl(assetKind, angle, new Set(urls))
  }, [requestedUrl, urls, assetKind, angle])

  // Direct imperative className mutation on every angle change avoids
  // React reconciliation in the swap hot path.
  const imgRefs = useRef({})
  useEffect(() => {
    for (const url of urls) {
      const el = imgRefs.current[url]
      if (!el) continue
      el.className = url === activeUrl ? 'isf-frame isf-frame--active' : 'isf-frame'
    }
  }, [activeUrl, urls])

  // <link rel="preload"> for each PNG — kicks the browser fetcher in
  // parallel with Image.decode(), trimming cold-start TTI by 200-500ms
  // on a typical 4G connection.
  useEffect(() => {
    if (typeof document === 'undefined' || !assetKind) return
    const head = document.head
    const links = listUrls(assetKind).map((url) => {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'image'
      link.href = url
      head.appendChild(link)
      return link
    })
    return () => { links.forEach((l) => head.removeChild(l)) }
  }, [assetKind])

  if (!assetKind) return null

  return (
    <>
      <div
        className='isf-root'
        style={{
          position: 'relative',
          width: '100%',
          maxWidth,
          // Taller than KneeROMFigure's 200/210 so the angle label at 90°
          // (which sits at SVG-y ≈ 234 with KNEE_Y = 120) has room to render
          // below the bands without being clipped by the container edge.
          // A kind may set its own, as in the patient app (Mini Squat).
          aspectRatio: anchor.aspectRatio || '200 / 210',
          margin: '0 auto',
          // `contain: layout` only — `paint` would clip the label that
          // overflows the dial's SVG viewBox.
          contain: 'layout',
          background: background || (ready ? 'transparent' : '#fafafa'),
        }}
        role='img'
        aria-label={`Avatar at ${Math.round(angle)} degrees`}
      >
        {/* ROM band dial sits BEHIND the PNG stack (rendered first → lower
            paint order). PNG body covers the band sectors that overlap it;
            the red tick and angle label sit at the outer band edge and
            remain visible past the body silhouette. */}
        {anchor.bandLayout === 'linear' ? null : (
          <div className='isf-dial'>
            <RomBandDial
              liveAngle={liveAngle}
              bandRanges={bandRanges}
              posture={posture}
              showLabel={false}
              goalAngles={goalAngles}
              yellowOutline={yellowOutline}
              radialAngles={radialAngles}
              {...anchor}
            />
          </div>
        )}
        <div className='isf-stack' style={stackStyle}>
          {/* Gate stack rendering on `ready` (= preloadKind resolved). Without
              the gate, each <img> mounts before its bitmap is decoded and
              "pops" into the active slot as decoding completes → visible
              flashes during the first session of the page-load. Subsequent
              opens are instant because the module-singleton decode cache
              in imageDecodeCache.js survives unmounts. */}
          {ready && urls.map((url) => (
            <img
              key={url}
              ref={(el) => { if (el) imgRefs.current[url] = el }}
              src={url}
              alt=''
              className={url === activeUrl ? 'isf-frame isf-frame--active' : 'isf-frame'}
              // Active frame decodes synchronously so a decode miss delays the
              // swap rather than painting a blank figure. Kept in sync with the
              // patient app's copy of this component.
              decoding={url === activeUrl ? 'sync' : 'async'}
              draggable={false}
            />
          ))}
        </div>
        {/* Live-angle degrees pinned to bottom-center of the figure
            container in SCREEN pixels (not scaled with the dial). */}
        {liveAngle != null && (
          <div className='isf-angle-label'>
            {Math.round(labelAngle != null ? labelAngle : liveAngle)}°
          </div>
        )}
        {!ready && <div className='isf-loading' aria-hidden>…</div>}
      </div>
      {anchor.bandLayout === 'linear' && (
        <LinearBandBar
          liveAngle={liveAngle}
          bandRanges={bandRanges}
        />
      )}
    </>
  )
}
